import { useRef, useState } from 'react';

const BASE = import.meta.env.BASE_URL;
const VIDEO_SRC = `${BASE}video.mp4`;
const POSTER_SRC = `${BASE}video-poster.jpg`;

const highlights = [
  { value: '3,000 HP', label: 'ZJ90DBS land rig class' },
  { value: '2', label: 'OEM partners on this build' },
  { value: '70+ yrs', label: 'LSPE manufacturing heritage' },
  { value: 'Jul 2025', label: 'Delivery ceremony' },
];

export default function VideoShowcase() {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [playing, setPlaying] = useState(false);

  const handlePlay = () => {
    const video = videoRef.current;
    if (!video) return;
    setPlaying(true);
    video.play().catch(() => setPlaying(false));
  };

  return (
    <section
      id="video-showcase"
      aria-labelledby="video-showcase-title"
      className="relative overflow-hidden bg-[#010C19] py-20 lg:py-32"
    >
      {/* Ambient glows + grid texture */}
      <div aria-hidden className="pointer-events-none absolute inset-0">
        <div className="absolute -top-40 left-1/2 h-[520px] w-[900px] -translate-x-1/2 rounded-full bg-[#0875D4]/25 blur-[140px]" />
        <div className="absolute bottom-[-200px] right-[-120px] h-[420px] w-[420px] rounded-full bg-[#E37D33]/15 blur-[120px]" />
        <div
          className="absolute inset-0 opacity-[0.06]"
          style={{
            backgroundImage:
              'linear-gradient(#ffffff 1px, transparent 1px), linear-gradient(90deg, #ffffff 1px, transparent 1px)',
            backgroundSize: '64px 64px',
            maskImage: 'radial-gradient(ellipse at center, black 30%, transparent 75%)',
            WebkitMaskImage: 'radial-gradient(ellipse at center, black 30%, transparent 75%)',
          }}
        />
      </div>

      <div className="relative z-10 mx-auto max-w-[1160px] px-6 sm:px-8 lg:px-12">
        {/* Heading */}
        <div className="mx-auto mb-12 max-w-3xl text-center lg:mb-16">
          <span className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-4 py-1.5 text-sm font-medium text-[#E37D33] backdrop-blur">
            <span className="relative flex h-2 w-2">
              <span className="absolute inline-flex h-full w-full rounded-full bg-[#E37D33] opacity-75 motion-safe:animate-ping" />
              <span className="relative inline-flex h-2 w-2 rounded-full bg-[#E37D33]" />
            </span>
            Partners in action
          </span>
          <h2
            id="video-showcase-title"
            className="mt-5 text-3xl font-medium leading-tight text-white sm:text-[42px]"
          >
            Delivering 3,000 HP of <br className="hidden sm:block" />
            <span className="bg-gradient-to-r from-[#4FA8F5] to-[#0875D4] bg-clip-text text-transparent">
              world-class drilling power
            </span>
          </h2>
          <p className="mt-5 text-lg leading-relaxed text-[#9CA3AF]">
            Watch the official delivery ceremony of the ZJ90DBS (3000HP) drilling rig and supporting equipment —
            built by our OEM partners Lanzhou LS Petroleum Equipment and Brightway Energy.
          </p>
        </div>

        {/* Video frame */}
        <div className="group relative mx-auto max-w-[1000px]">
          {/* Gradient border glow */}
          <div
            aria-hidden
            className="absolute -inset-px rounded-[22px] bg-gradient-to-br from-[#0875D4]/70 via-white/10 to-[#E37D33]/50 opacity-70 blur-[2px] transition-opacity duration-500 group-hover:opacity-100"
          />
          <div className="relative overflow-hidden rounded-[20px] bg-black shadow-[0_40px_120px_-20px_rgba(8,117,212,0.45)]">
            <video
              ref={videoRef}
              className="aspect-video w-full object-cover"
              src={VIDEO_SRC}
              poster={POSTER_SRC}
              preload="none"
              playsInline
              controls={playing}
              onPlay={() => setPlaying(true)}
              onEnded={() => setPlaying(false)}
            >
              Your browser does not support HTML5 video.
            </video>

            {/* Poster overlay */}
            {!playing && (
              <button
                id="video-showcase-play"
                type="button"
                onClick={handlePlay}
                aria-label="Play the ZJ90DBS rig delivery ceremony video"
                className="absolute inset-0 flex cursor-pointer items-center justify-center"
              >
                <img
                  src={POSTER_SRC}
                  alt=""
                  className="absolute inset-0 h-full w-full object-cover transition-transform duration-[1.2s] ease-out group-hover:scale-[1.04]"
                />
                <span className="absolute inset-0 bg-gradient-to-t from-[#010C19]/90 via-[#010C19]/30 to-[#022843]/20" />

                {/* Play button */}
                <span className="relative flex h-20 w-20 items-center justify-center sm:h-24 sm:w-24">
                  <span className="absolute inset-0 rounded-full bg-white/30 motion-safe:animate-ping [animation-duration:2s]" />
                  <span className="absolute -inset-3 rounded-full border border-white/30 backdrop-blur-sm" />
                  <span className="relative flex h-full w-full items-center justify-center rounded-full bg-white text-[#00458F] shadow-2xl transition-transform duration-300 group-hover:scale-110">
                    <i className="ri-play-fill ml-1 text-4xl sm:text-5xl" />
                  </span>
                </span>

                {/* Caption bar */}
                <span className="absolute inset-x-0 bottom-0 flex flex-col gap-3 p-5 text-left sm:flex-row sm:items-end sm:justify-between sm:p-8">
                  <span>
                    <span className="mb-2 inline-block rounded-full bg-[#0875D4] px-3 py-1 text-xs font-medium uppercase tracking-wide text-white">
                      Delivery ceremony
                    </span>
                    <span className="block text-lg font-medium text-white sm:text-2xl">
                      ZJ90DBS (3000HP) Drilling Rig &amp; Supporting Equipment
                    </span>
                  </span>
                  <span className="inline-flex shrink-0 items-center gap-2 self-start rounded-full bg-black/40 px-3 py-1.5 text-sm text-white/90 backdrop-blur sm:self-auto">
                    <i className="ri-time-line" /> 1:04
                  </span>
                </span>
              </button>
            )}
          </div>
        </div>

        {/* Highlights */}
        <div className="mx-auto mt-12 grid max-w-[1000px] grid-cols-2 gap-4 lg:mt-16 lg:grid-cols-4">
          {highlights.map((h) => (
            <div
              key={h.label}
              className="rounded-xl border border-white/10 bg-white/[0.04] p-5 backdrop-blur transition-all duration-300 hover:-translate-y-1 hover:border-[#0875D4]/60 hover:bg-white/[0.07] lg:p-6"
            >
              <p className="text-2xl font-medium text-white lg:text-3xl">{h.value}</p>
              <p className="mt-1 text-sm text-[#9CA3AF]">{h.label}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
